import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Download,
  ArrowRight,
  Sparkles,
  Zap,
  Code2,
  Terminal,
  GitBranch,
  Puzzle,
  MessageSquare,
  Search,
  FileCode,
  Settings,
  Palette,
  Globe,
  Shield,
  Cpu,
  Layers,
  Bug,
  TestTube,
  Database,
  Cloud,
  ChevronRight,
  Play,
  Check,
  Keyboard,
  Command,
  MousePointer2,
  Workflow,
  Bot,
  Brain,
  Gem,
  Bolt,
  FlaskConical,
  ClipboardCheck,
  CheckCircle2,
  Target,
  Gauge,
} from 'lucide-react';
import { CodeDemo } from '@/components/ui/CodeDemo';
import { AgentBackground } from '@/components/ui/AgentBackground';

const featureIds = ['agent', 'autocomplete', 'codebase', 'testing', 'terminal', 'git', 'debug'] as const;
const featureIcons = [Sparkles, Zap, Code2, FlaskConical, Terminal, GitBranch, Bug];
const featureColors = [
  'from-purple-500 to-pink-500',
  'from-purple-500 to-fuchsia-500',
  'from-green-500 to-emerald-500',
  'from-orange-500 to-amber-500',
  'from-slate-500 to-zinc-600',
  'from-rose-500 to-pink-600',
  'from-red-500 to-orange-500'
];

const additionalFeatureIcons = [Terminal, GitBranch, Puzzle, MessageSquare, Search, FileCode, Settings, Palette, Bug, TestTube, Database, Cloud];
const additionalGradients = [
  'from-orange-500 to-red-500',
  'from-pink-500 to-rose-500',
  'from-violet-500 to-purple-500',
  'from-blue-500 to-indigo-500',
  'from-violet-500 to-purple-500',
  'from-green-500 to-lime-500',
  'from-yellow-500 to-orange-500',
  'from-fuchsia-500 to-pink-500',
  'from-red-500 to-orange-500',
  'from-emerald-500 to-green-500',
  'from-indigo-500 to-blue-500',
  'from-indigo-500 to-violet-500',
];

const modelOptions = [
  { name: 'GPT-4.5 Turbo', provider: 'OpenAI', color: 'bg-gradient-to-br from-green-500 to-emerald-600', icon: Bot, description: 'Latest multimodal model' },
  { name: 'Claude Opus 4.5', provider: 'Anthropic', color: 'bg-gradient-to-br from-orange-500 to-amber-600', icon: Brain, description: 'Most capable reasoning' },
  { name: 'Gemini 2.5 Pro', provider: 'Google', color: 'bg-gradient-to-br from-blue-500 to-indigo-600', icon: Gem, description: 'Advanced multimodal AI' },
  { name: 'Grok 3', provider: 'xAI', color: 'bg-gradient-to-br from-purple-500 to-violet-600', icon: Bolt, description: 'Real-time knowledge' },
];

function FeatureTab({ feature, isActive, onClick }: {
  feature: { id: string; icon: React.ElementType; title: string; subtitle: string; [key: string]: unknown };
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-5 py-4 rounded-xl text-left transition-all w-full ${
        isActive
          ? 'bg-accent text-white shadow-lg shadow-accent/25'
          : 'bg-bg-card hover:bg-bg-elevated border border-border-default'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        isActive ? 'bg-white/20' : 'bg-accent-muted'
      }`}>
        <feature.icon size={20} className={isActive ? 'text-white' : 'text-accent'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{feature.title}</div>
        <div className={`text-sm truncate ${isActive ? 'text-white/70' : 'text-text-tertiary'}`}>
          {feature.subtitle}
        </div>
      </div>
      <ChevronRight size={18} className={isActive ? 'text-white/70' : 'text-text-tertiary'} />
    </button>
  );
}

export function FeaturesPage() {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  // Build features from translations
  const mainFeatures = featureIds.map((id, index) => {
    const featureKey = id === 'agent' ? 'aiAgent' : id === 'autocomplete' ? 'autocomplete' : id;
    return {
      id,
      icon: featureIcons[index],
      title: t(`features.${featureKey}.title`),
      subtitle: t(`features.${featureKey}.subtitle`),
      description: t(`features.${featureKey}.description`),
      features: t(`features.${featureKey}.features`, { returnObjects: true }) as string[],
      color: featureColors[index]
    };
  });

  const additionalFeatureKeys = [
    'integratedTerminal', 'gitIntegration', 'extensionEcosystem', 'aiChat',
    'smartSearch', 'multiLanguage', 'customizable', 'themes',
    'debugging', 'testingTitle', 'database', 'cloudSync'
  ];

  const additionalFeatures = additionalFeatureKeys.map((key, i) => ({
    icon: additionalFeatureIcons[i],
    title: t(`features.additional.${key}`),
    description: t(`features.additional.${key === 'testingTitle' ? 'testingDesc' : key.replace(/([A-Z])/g, '') + 'Desc'}`),
    gradient: additionalGradients[i]
  }));

  // Fix descriptions mapping
  const additionalFeaturesFixed = [
    { icon: Terminal, title: t('features.additional.integratedTerminal'), description: t('features.additional.terminalDesc'), gradient: 'from-orange-500 to-red-500' },
    { icon: GitBranch, title: t('features.additional.gitIntegration'), description: t('features.additional.gitDesc'), gradient: 'from-pink-500 to-rose-500' },
    { icon: Puzzle, title: t('features.additional.extensionEcosystem'), description: t('features.additional.extensionDesc'), gradient: 'from-violet-500 to-purple-500' },
    { icon: MessageSquare, title: t('features.additional.aiChat'), description: t('features.additional.aiChatDesc'), gradient: 'from-blue-500 to-indigo-500' },
    { icon: Search, title: t('features.additional.smartSearch'), description: t('features.additional.searchDesc'), gradient: 'from-violet-500 to-purple-500' },
    { icon: FileCode, title: t('features.additional.multiLanguage'), description: t('features.additional.multiLangDesc'), gradient: 'from-green-500 to-lime-500' },
    { icon: Settings, title: t('features.additional.customizable'), description: t('features.additional.customizableDesc'), gradient: 'from-yellow-500 to-orange-500' },
    { icon: Palette, title: t('features.additional.themes'), description: t('features.additional.themesDesc'), gradient: 'from-fuchsia-500 to-pink-500' },
    { icon: Bug, title: t('features.additional.debugging'), description: t('features.additional.debuggingDesc'), gradient: 'from-red-500 to-orange-500' },
    { icon: TestTube, title: t('features.additional.testingTitle'), description: t('features.additional.testingDesc'), gradient: 'from-emerald-500 to-green-500' },
    { icon: Database, title: t('features.additional.database'), description: t('features.additional.databaseDesc'), gradient: 'from-indigo-500 to-blue-500' },
    { icon: Cloud, title: t('features.additional.cloudSync'), description: t('features.additional.cloudSyncDesc'), gradient: 'from-indigo-500 to-violet-500' },
  ];

  const shortcuts = [
    { keys: ['⌘', 'K'], action: t('features.shortcuts.openAiChat'), description: t('features.shortcuts.startConversation') },
    { keys: ['⌘', 'L'], action: t('features.shortcuts.generateCode'), description: t('features.shortcuts.aiGenerates') },
    { keys: ['⌘', 'I'], action: t('features.shortcuts.inlineEdit'), description: t('features.shortcuts.editWithAi') },
    { keys: ['Tab'], action: t('features.shortcuts.acceptSuggestion'), description: t('features.shortcuts.acceptAutocomplete') },
    { keys: ['⌘', 'Shift', 'E'], action: t('features.shortcuts.explainCode'), description: t('features.shortcuts.aiExplains') },
    { keys: ['⌘', 'Shift', 'R'], action: t('features.shortcuts.refactor'), description: t('features.shortcuts.aiRefactors') },
  ];

  const stats = [
    { value: '10x', label: t('features.stats.fasterCoding'), description: t('features.stats.avgSpeedImprovement') },
    { value: '500K+', label: t('features.stats.developers'), description: t('features.stats.usingDaily') },
    { value: '50+', label: t('features.stats.languages'), description: t('features.stats.fullySupported') },
    { value: '99.9%', label: t('features.stats.uptime'), description: t('features.stats.enterpriseReliability') },
  ];

  return (
    <>
      <SEO {...SEO_CONFIG.features} />
      <div className="pt-24">
      {/* Hero Section */}
      <section className="section-lg relative overflow-hidden min-h-[700px] flex items-center">
        <AgentBackground />
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6 animate-pulse">
              <Sparkles size={16} />
              <span>{t('features.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('features.title')}{' '}
              <span className="relative">
                <span className="gradient-text">{t('features.titleHighlight')}</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10C50 4 100 4 150 7C200 10 250 6 298 3" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop offset="0%" stopColor="#6366f1"/>
                      <stop offset="100%" stopColor="#a855f7"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h1>

            <p className="text-xl text-text-secondary mb-10 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Link to="/download" className="btn btn-primary btn-lg group">
                <Download size={20} />
                {t('features.downloadCta')}
                <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button className="btn btn-secondary btn-lg group">
                <Play size={18} />
                {t('features.watchDemo')}
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold gradient-text mb-1">
                    {stat.value}
                  </div>
                  <div className="font-medium text-text-primary">{stat.label}</div>
                  <div className="text-sm text-text-tertiary">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Feature Showcase */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('features.sectionTitle')}{' '}
              <span className="gradient-text">{t('features.sectionHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('features.sectionSubtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Feature Tabs */}
            <div className="lg:col-span-2 space-y-3">
              {mainFeatures.map((feature, index) => (
                <FeatureTab
                  key={feature.id}
                  feature={feature}
                  isActive={activeFeature === index}
                  onClick={() => setActiveFeature(index)}
                />
              ))}
            </div>

            {/* Feature Content */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl border border-border-default overflow-hidden bg-bg-card">
                {/* Demo */}
                <div className="h-[400px] relative">
                  <CodeDemo demoType={mainFeatures[activeFeature].id as 'agent' | 'autocomplete' | 'codebase'} />
                  <div className={`absolute inset-0 bg-gradient-to-t ${mainFeatures[activeFeature].color} opacity-5 pointer-events-none`} />
                </div>

                {/* Feature Details */}
                <div className="p-6 border-t border-border-default bg-bg-elevated">
                  <h3 className="text-lg font-semibold mb-4">
                    {mainFeatures[activeFeature].description}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {mainFeatures[activeFeature].features.map((item) => (
                      <div key={item} className="flex items-center gap-2 text-sm text-text-secondary">
                        <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                          <Check size={12} className="text-success" />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Keyboard Shortcuts */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
                <Keyboard size={14} />
                {t('features.keyboard.badge')}
              </div>
              <h2 className="heading-lg mb-4">
                {t('features.keyboard.title')}{' '}
                <span className="gradient-text">{t('features.keyboard.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary mb-6">
                {t('features.keyboard.subtitle')}
              </p>

              <div className="space-y-4">
                {shortcuts.slice(0, 4).map((shortcut, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-bg-surface border border-border-default group hover:border-accent/30 transition-colors">
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, ki) => (
                        <span key={ki}>
                          <kbd className="px-2.5 py-1.5 text-sm font-medium bg-bg-card border border-border-default rounded-lg shadow-sm group-hover:border-accent/30 transition-colors">
                            {key}
                          </kbd>
                          {ki < shortcut.keys.length - 1 && <span className="text-text-tertiary mx-1">+</span>}
                        </span>
                      ))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{shortcut.action}</div>
                      <div className="text-sm text-text-tertiary truncate">{shortcut.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-purple-500/20 blur-3xl opacity-30" />
              <div className="relative rounded-2xl border border-border-default overflow-hidden bg-bg-card p-8">
                <div className="text-center mb-8">
                  <Command className="mx-auto mb-4 text-accent" size={48} />
                  <h3 className="text-xl font-semibold mb-2">{t('features.keyboard.commandPalette')}</h3>
                  <p className="text-text-secondary">{t('features.keyboard.pressToOpen')} <kbd className="px-2 py-1 bg-bg-elevated rounded border border-border-default text-sm">⌘K</kbd></p>
                </div>

                <div className="space-y-2">
                  {shortcuts.map((shortcut, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-bg-elevated hover:bg-accent/10 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <MousePointer2 size={16} className="text-text-tertiary group-hover:text-accent transition-colors" />
                        <span>{shortcut.action}</span>
                      </div>
                      <div className="flex gap-1">
                        {shortcut.keys.map((key, ki) => (
                          <kbd key={ki} className="px-2 py-0.5 text-xs bg-bg-card border border-border-default rounded">
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model Selection */}
      <section className="section bg-bg-surface relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-purple-500/5" />
        <div className="container relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
              <Cpu size={14} />
              {t('features.aiModels.badge')}
            </div>
            <h2 className="heading-lg mb-4">
              {t('features.aiModels.title')}{' '}
              <span className="gradient-text">{t('features.aiModels.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('features.aiModels.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {modelOptions.map((model, i) => (
              <div
                key={model.name}
                className="relative group"
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className={`absolute inset-0 ${model.color} rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                <div className="relative rounded-2xl border border-border-default bg-bg-card p-6 hover:border-accent/30 transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-16 h-16 ${model.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                    <model.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold mb-1">{model.name}</h3>
                  <p className="text-text-tertiary text-sm mb-1">{model.provider}</p>
                  <p className="text-text-secondary text-xs">{model.description}</p>

                  <div className={`mt-4 flex items-center gap-2 text-sm font-medium transition-all duration-300 ${
                    hoveredCard === i ? 'text-accent translate-x-1' : 'text-text-tertiary'
                  }`}>
                    <span>{t('features.learnMore')}</span>
                    <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
              <Workflow size={14} />
              {t('features.additional.badge')}
            </div>
            <h2 className="heading-lg mb-4">
              {t('features.additional.title')}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('features.additional.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {additionalFeaturesFixed.map((feature, i) => (
              <div
                key={i}
                className="group relative rounded-xl border border-border-default bg-bg-card p-5 hover:border-accent/30 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 blur-2xl transition-opacity duration-500`} />
                <div className={`relative w-10 h-10 rounded-lg bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="text-white" size={20} />
                </div>
                <h3 className="font-semibold mb-1 relative">{feature.title}</h3>
                <p className="text-text-secondary text-sm relative">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built-in Testing - Unique Feature */}
      <section className="section bg-bg-surface relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-amber-500/5" />
        <div className="container relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warning/10 border border-warning/20 text-warning text-sm mb-4">
                <Target size={16} />
                <span>{t('features.builtInTesting.badge')}</span>
              </div>
              <h2 className="heading-lg mb-4">
                {t('features.builtInTesting.title')}{' '}
                <span className="gradient-text">{t('features.builtInTesting.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                {t('features.builtInTesting.subtitle')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Unit Testing */}
              <div className="rounded-2xl border border-border-default bg-bg-card p-8 hover:border-accent/30 transition-all group">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <FlaskConical className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('features.builtInTesting.unitTesting.title')}</h3>
                <p className="text-text-secondary mb-6">
                  {t('features.builtInTesting.unitTesting.description')}
                </p>
                <ul className="space-y-3">
                  {(t('features.builtInTesting.unitTesting.features', { returnObjects: true }) as string[]).map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-text-secondary">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* UAT Testing */}
              <div className="rounded-2xl border border-border-default bg-bg-card p-8 hover:border-accent/30 transition-all group">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <ClipboardCheck className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('features.builtInTesting.uatTesting.title')}</h3>
                <p className="text-text-secondary mb-6">
                  {t('features.builtInTesting.uatTesting.description')}
                </p>
                <ul className="space-y-3">
                  {(t('features.builtInTesting.uatTesting.features', { returnObjects: true }) as string[]).map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-text-secondary">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Comparison Badge */}
            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-accent/10 to-purple-500/10 border border-accent/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                    <Gauge className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">{t('features.builtInTesting.whyUnique')}</h4>
                    <p className="text-text-secondary text-sm">{t('features.builtInTesting.uniqueReason')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-success">
                  <CheckCircle2 size={20} />
                  <span className="font-medium">{t('features.builtInTesting.onlyInOchtopus')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 text-success text-sm mb-4">
                <Shield size={14} />
                {t('features.security.badge')}
              </div>
              <h2 className="heading-lg mb-4">
                {t('features.security.title')}{' '}
                <span className="gradient-text">{t('features.security.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary mb-6">
                {t('features.security.subtitle')}
              </p>
              <ul className="space-y-3 mb-8">
                {(t('features.security.features', { returnObjects: true }) as string[]).map((item) => (
                  <li key={item} className="flex items-center gap-3 text-text-secondary">
                    <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center">
                      <Check size={12} className="text-success" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/enterprise" className="btn btn-secondary">
                {t('features.security.learnEnterprise')}
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Shield, label: 'SOC 2', color: 'from-green-500 to-emerald-600' },
                { icon: Globe, label: 'GDPR', color: 'from-blue-500 to-indigo-600' },
                { icon: Layers, label: 'CCPA', color: 'from-orange-500 to-amber-600' },
                { icon: Shield, label: 'ISO 27001', color: 'from-purple-500 to-violet-600' },
              ].map((item, i) => (
                <div key={i} className="aspect-square rounded-2xl border border-border-default bg-bg-card flex flex-col items-center justify-center group hover:border-accent/30 transition-colors">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                    <item.icon className="text-white" size={28} />
                  </div>
                  <span className="font-semibold">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-lg relative overflow-hidden" id="download">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-purple-500/10" />
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Download size={16} />
              <span>{t('features.cta.badge')}</span>
            </div>
            <h2 className="heading-xl mb-4">
              {t('features.cta.title')}{' '}
              <span className="gradient-text">{t('features.cta.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-text-secondary mb-10">
              {t('features.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/download" className="btn btn-primary btn-lg group">
                <Download size={20} />
                {t('features.cta.downloadMac')}
                <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/download" className="btn btn-secondary btn-lg">
                {t('features.cta.downloadWindows')}
              </Link>
              <Link to="/download" className="btn btn-secondary btn-lg">
                {t('features.cta.downloadLinux')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
